/**
 * @file Utilities to work with eihers.
 */
import { Either, identity, Left, Right } from 'purify-ts';
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
/**
 * Same as {@link Either.sequence} but returns all found lefts.
 *
 * @param eithers The eithers to check.
 * @returns A left containing an error of all left values found if one was
 * found, or a right containing all right values.
 */
export function sequence(eithers) {
    const lefts = [];
    const rights = [];
    for (const either of eithers) {
        either.caseOf({
            Left: err => {
                lefts.push(err);
            },
            Right: val => {
                if (lefts.length === 0) {
                    rights.push(val);
                }
            },
        });
    }
    if (NonEmptyList.isNonEmpty(lefts)) {
        return Left(lefts);
    }
    return Right(rights);
}
/**
 * Do an `and` on a list of values.
 *
 * @param val The either values to and.
 * @returns A {@link Right} containing an array of all the values in val, if
 * all values in val are rights, otherwise it is a left that contains an array
 * of {@link Maybe} values that are just if the either at that index was a left.
 */
export function and(...val) {
    if (val.some(x => x.isLeft())) {
        return Left(val.map(item => Maybe.fromEither(item.swap())));
    }
    return Right(val.map(item => item.extract()));
}
/**
 * Create an {@link Either} from a predicate function.
 *
 * @param p The predicate function.
 * @param ifTrue The value to use if `p` returns true, this value is also
 * provided to `p`.
 * @param ifFalse The value to use if `p` return false.
 * @returns An {@link Left} or {@link Right} depending on the return value of a
 * predicate.
 */
export function fromPredicate(p, ifTrue, ifFalse) {
    return Maybe.fromPredicate(p, ifTrue).toEither(ifFalse);
}
/**
 * Create an {@link Either} from a falsy value.
 *
 * @param value The maybe falsy value. If truthy used as the right value.
 * @param other The left value if `value` was falsy.
 * @returns A right or left depending on `value`.
 */
export function fromFalsy(value, other) {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return value ? Right(value) : Left(other);
}
/**
 * The error raised internally if a {@link Do} fails.
 */
class DoError extends Error {
    err;
    /** @param err The error that should be used in the left value. */
    constructor(err) {
        super('Got a Do error');
        this.err = err;
    }
    /**
     * Maybe throw a {@link DoError} based on the value of `val`.
     *
     * @param val The either value to check.
     * @returns The right value of `val` if it is a right.
     * @throws A {@link DoError} if `val` is a left.
     */
    static fromEither(val) {
        return val.caseOf({
            Left: err => {
                throw new DoError(err);
            },
            Right: identity,
        });
    }
}
/**
 * The `Do` monady for eithers.
 *
 * @param callback The callback to run.
 * @returns The value returned from `callback` wrapped in a right if it
 * succeeded, otherwise a left.
 */
export function Do(callback) {
    try {
        const val = callback({
            tryRight: DoError.fromEither,
            throwE: err => {
                throw new DoError(err);
            },
        });
        return Either.of(val);
    }
    catch (err) {
        if (err instanceof DoError) {
            return Left(err.err);
        }
        throw err;
    }
}
/**
 * Check if two eithers are equal.
 *
 * @param eitherA The first value to compare.
 * @param eitherB The second value to compare.
 * @param cmpL The way to compare two lefts.
 * @param cmpR The way to compare two right.
 * @returns If the two eithers should be considered the same, this is the case when they are both of
 * the same side, and the comparison function for that side returns `true`.
 */
export function equals(eitherA, eitherB, cmpL, cmpR) {
    return eitherA.caseOf({
        Left: la => eitherB.caseOf({
            Left: lb => cmpL(la, lb),
            Right: () => false,
        }),
        Right: ra => eitherB.caseOf({
            Right: rb => cmpR(ra, rb),
            Left: () => false,
        }),
    });
}
